
import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';

interface PaymentFormProps {
  amount: number;
  currency: string;
  planName: string;
  customerData: {
    email: string;
    name: string;
    phone: string;
    planId: string;
    gymId: string;
    surname: string;
    password: string;
  };
  onSuccess: (paymentIntentId: string | null, clientSecret: string) => void;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,
};

export function PaymentForm({
  amount,
  currency,
  planName,
  customerData,
  onSuccess,
}: PaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { toast } = useToast();

  const formatPrice = (price: number, curr: string = 'EUR') => {
    if (curr === 'ALL') {
      return `${price.toLocaleString()} ALL`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(price);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    const card = elements.getElement(CardElement);

    if (!card) {
      setIsProcessing(false);
      return;
    }

    try {
      console.log('Step 1: Creating payment method...');
      
      // Create payment method from card details
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: card,
        billing_details: {
          name: `${customerData.name} ${customerData.surname}`,
          email: customerData.email,
        },
      });

      if (methodError) {
        setPaymentError(methodError.message || t('auth.payment.invalidCardDetails'));
        toast({
          title: t('auth.payment.invalidCardDetailsTitle'),
          description: methodError.message || t('auth.payment.checkCardInfo'),
          variant: 'destructive',
        });
        setIsProcessing(false);
        return;
      }

      console.log('Step 2: Registering user with backend...');
      
      // Step 1: Register user with backend (user will be INACTIVE)
      const registrationPayload = {
        email: customerData.email,
        phone: customerData.phone,
        password: customerData.password,
        name: customerData.name,
        surname: customerData.surname,
        planId: customerData.planId,
        gymId: customerData.gymId, // Keep as string, not array
        userType: 'user', // Always 'user' for public registration
        // Add amount and currency to override backend calculation
        amount: amount, // This is already divided by 100 in euros
        currency: currency, // This should be 'EUR'
      };

      console.log('Registration payload being sent:', registrationPayload);

      const registrationResponse = await apiClient.post('/auth/register-with-payment', registrationPayload);

      console.log('Registration response status:', registrationResponse.status);
      console.log('Registration response data:', registrationResponse.data);

      // apiClient handles errors automatically, so if we reach here, it was successful
      const registrationData = registrationResponse.data;
      
      console.log('Registration response:', registrationData);
      
      if (!registrationData.results || !registrationData.results.user) {
        console.error('No user in response:', registrationData);
        throw new Error(registrationData.message || 'Registration failed - no user data received');
      }

      const { payment } = registrationData.results;
      const clientSecret = payment.clientSecret;
      
      if (!clientSecret) {
        throw new Error('No client secret received from backend');
      }
      
      console.log('Step 3: Confirming payment with Stripe...');
      console.log('Client secret:', clientSecret);
      
      // Step 2: Confirm payment with Stripe using the clientSecret from backend
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
      });

      console.log('Stripe confirmation result:', { confirmError, paymentIntent });

      if (confirmError) {
        console.error('Stripe confirmation error:', confirmError);
        throw new Error(confirmError.message || 'Payment confirmation failed');
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Step 4: Confirming payment with backend...');
        console.log('Payment Intent ID:', paymentIntent.id);
        
        // Step 3: Confirm payment with backend to activate user
        const confirmResponse = await apiClient.post('/auth/confirm-payment', {
          paymentIntentId: paymentIntent.id,
        });

        console.log('Confirm payment response status:', confirmResponse.status);
        console.log('Confirm payment response data:', confirmResponse.data);

        const confirmData = confirmResponse.data;
        console.log('Confirm payment response data:', confirmData);
        
        // Check if we have the results structure with user data
        if (!confirmData.results || !confirmData.results.user) {
          throw new Error(confirmData.message || 'Payment confirmation failed');
        }

        toast({
          title: t('auth.payment.registrationComplete'),
          description: t('auth.payment.welcomeMessage'),
        });
        
        // Call onSuccess to move to the success step
        onSuccess(paymentIntent.id, clientSecret);
      } else {
        throw new Error('Payment was not successful');
      }

    } catch (err: any) {
      console.error('Registration/Payment error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
      
      setPaymentError(err.message || t('auth.payment.unexpectedError'));
      toast({
        title: t('auth.payment.registrationFailed'),
        description: err.message || t('auth.payment.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {t('auth.payment.orderSummary')}
            <Badge variant="outline">{formatPrice(amount, "EUR")}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">{planName}</p>
                <p className="text-sm text-gray-500">{t('auth.payment.membershipPlan')}</p>
              </div>
              <p className="font-medium">{formatPrice(amount, "EUR")}</p>
            </div>

            <Separator />

            <div className="flex justify-between items-center font-semibold">
              <p>{t('auth.payment.total')}</p>
              <p>{formatPrice(amount, "EUR")}</p>
            </div>

            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span>{t('auth.payment.instantActivation')}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5" />
            <span>{t('auth.payment.paymentInformation')}</span>
          </CardTitle>
          <CardDescription>
            {t('auth.payment.securePaymentDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="card-element" className="text-sm font-medium">
                {t('auth.payment.cardDetails')}
              </label>
              <div className="border rounded-md p-3 bg-white">
                <CardElement id="card-element" options={cardElementOptions} />
              </div>
            </div>

            {paymentError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{paymentError}</p>
              </div>
            )}

            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <Lock className="w-4 h-4" />
                <span>{t('auth.payment.securePayment')}</span>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!stripe || isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <span>{t('auth.payment.processing')}</span>
                </div>
              ) : (
                `${t('auth.payment.completeRegistration')} - ${formatPrice(amount, "EUR")}`
              )}
            </Button>

            <div className="text-xs text-gray-500 text-center space-y-1">
              <p>
                {t('auth.payment.termsAgreement')}
              </p>
              <p>{t('auth.payment.poweredByStripe')}</p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
        <div className="space-y-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-sm font-medium">{t('auth.payment.securePaymentTitle')}</p>
          <p className="text-xs text-gray-500">{t('auth.payment.sslEncryption')}</p>
        </div>
        <div className="space-y-2">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-4 h-4 text-green-600" />
          </div>
          <p className="text-sm font-medium">{t('auth.payment.instantActivationTitle')}</p>
          <p className="text-xs text-gray-500">
            {t('auth.payment.accessImmediately')}
          </p>
        </div>
        <div className="space-y-2">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
            <CreditCard className="w-4 h-4 text-purple-600" />
          </div>
          <p className="text-sm font-medium">{t('auth.payment.noHiddenFees')}</p>
          <p className="text-xs text-gray-500">{t('auth.payment.whatYouSee')}</p>
        </div>
      </div>
    </div>
  );
}
